import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { componentStyles } from '@/lib/theme'
import useIsMobile from '@/hooks/useIsMobile'

interface ResetPasswordFormProps {
  error: string | null
  success: string | null
  isLoading: boolean
  sessionEstablished: boolean
  onPasswordChange: (password: string, confirmPassword: string) => void
  onSubmit: (form: HTMLFormElement) => void
  onBackToLogin: () => void
}

export default function ResetPasswordForm({
  error,
  success,
  isLoading,
  sessionEstablished,
  onPasswordChange,
  onSubmit,
  onBackToLogin,
}: ResetPasswordFormProps) {
  const { t } = useTranslation()
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()
  const [localPassword, setLocalPassword] = useState('')

  // Mobile styles - keep comfortable sizes for touch
  const mobileStyles = isMobile ? {
    heading: { fontSize: '16px' },
    subheading: { fontSize: '13px', marginBottom: '12px' },
    label: { fontSize: '13px' },
    input: { fontSize: '14px', padding: '10px 12px' },
    button: { fontSize: '13px', padding: '10px 14px' },
    message: { fontSize: '12px' },
    link: { fontSize: '13px' },
  } : {}

  return (
    <>
      <h2 style={{
        ...componentStyles.authWindow.heading,
        color: colors.primaryText,
        fontSize: isMobile ? '14px' : '1.125rem',
        fontWeight: 600,
        marginBottom: '4px',
        ...mobileStyles.heading,
      }}>
        Reset Password
      </h2>
      <p style={{
        ...componentStyles.authWindow.heading,
        color: colors.secondaryText,
        fontSize: isMobile ? '11px' : '0.875rem',
        marginBottom: isMobile ? '8px' : '12px',
        ...mobileStyles.subheading,
      }}>
        {!sessionEstablished ? 'Verifying reset link...' : t('auth.resetPassword.description')}
      </p>
      <form style={{...componentStyles.authWindow.form, gap: isMobile ? '10px' : '16px', marginTop: isMobile ? '8px' : '16px'}}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '4px' : '6px' }}>
          <label
            htmlFor="password"
            style={{
              fontSize: isMobile ? '11px' : '0.875rem',
              fontWeight: 500,
              color: colors.primaryText,
              ...mobileStyles.label,
            }}
          >
            {t('auth.resetPassword.password')}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            onChange={(e) => {
              setLocalPassword(e.target.value)
              const confirmPassword =
                (document.getElementById('confirmPassword') as HTMLInputElement)
                  ?.value || ''
              if (confirmPassword) {
                onPasswordChange(e.target.value, confirmPassword)
              }
            }}
            style={{
              ...componentStyles.authWindow.input,
              border: `1px solid ${colors.primaryBorder}`,
              background: colors.primaryBg,
              color: colors.primaryText,
              width: '100%',
              ...mobileStyles.input,
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '4px' : '6px' }}>
          <label
            htmlFor="confirmPassword"
            style={{
              fontSize: isMobile ? '11px' : '0.875rem',
              fontWeight: 500,
              color: colors.primaryText,
              ...mobileStyles.label,
            }}
          >
            {t('auth.resetPassword.confirmPassword')}
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            onChange={(e) => {
              onPasswordChange(localPassword, e.target.value)
            }}
            style={{
              ...componentStyles.authWindow.input,
              border: `1px solid ${colors.primaryBorder}`,
              background: colors.primaryBg,
              color: colors.primaryText,
              width: '100%',
              ...mobileStyles.input,
            }}
          />
        </div>
        <div style={{ minHeight: isMobile ? '16px' : '20px', marginTop: '4px' }}>
          {error && (
            <p style={{ ...componentStyles.authWindow.errorMessage, color: colors.errorBorder, margin: 0, ...mobileStyles.message }}>{error}</p>
          )}
          {success && (
            <p style={{ ...componentStyles.authWindow.successMessage, color: colors.successBorder, margin: 0, ...mobileStyles.message }}>{success}</p>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            const form = e.currentTarget.closest('form')
            if (form) onSubmit(form)
          }}
          disabled={isLoading || !sessionEstablished}
          style={{
            width: '100%',
            borderRadius: 0,
            padding: isMobile ? '6px 10px' : '10px 15px',
            fontSize: isMobile ? '11px' : undefined,
            fontWeight: 500,
            cursor: (isLoading || !sessionEstablished) ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            background: colors.secondaryBg,
            color: (isLoading || !sessionEstablished) ? colors.tertiaryText : colors.primaryText,
            border: `1px solid ${colors.primaryBorder}`,
            marginTop: isMobile ? '4px' : '8px',
            ...mobileStyles.button,
          }}
        >
          {isLoading
            ? t('auth.processing')
            : !sessionEstablished
              ? t('auth.processing')
              : t('auth.resetPassword.submit')}
        </button>
        <div style={{ textAlign: 'center', marginTop: isMobile ? '8px' : '12px' }}>
          <button
            type="button"
            onClick={onBackToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primaryAccent,
              cursor: 'pointer',
              fontSize: isMobile ? '11px' : '0.875rem',
              textDecoration: 'none',
              fontFamily: 'inherit',
              padding: '4px 8px',
              ...mobileStyles.link,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none'
            }}
          >
            {t('auth.resetPassword.backToLogin')}
          </button>
        </div>
      </form>
    </>
  )
}
